import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState
} from "react";

export interface ConfirmModalProps {
  title: string;
  message: string | ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  onConfirm: () => Promise<void> | void;
  onCancel?: () => Promise<void> | void;
}

export interface AlertModalProps {
  title: string;
  message: string | ReactNode;
  confirmLabel: string;
  onConfirm: () => Promise<void> | void;
}

type ModalType = {
  confirm: ConfirmModalProps;
};

type ModalState =
  | { type: null; props: null; onClose: () => void }
  | {
      [K in keyof ModalType]: {
        type: K;
        props: ModalType[K];
        onClose: () => void;
      };
    }[keyof ModalType];

interface ModalContextValue {
  showModal: <T extends keyof ModalType>(type: T, props: ModalType[T]) => void;
  closeModal: () => void;
  modalState: ModalState | null;
}

const ModalContext = createContext<ModalContextValue | null>(null);

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [modalState, setModalState] = useState<ModalState | null>(null);

  const showModal = useCallback(
    <T extends keyof ModalType>(type: T, props: ModalType[T]) => {
      setModalState({
        type,
        props,
        onClose: () => setModalState(null)
      } as ModalState);
    },
    []
  );

  const closeModal = useCallback(() => {
    setModalState(null);
  }, []);

  const value = { showModal, closeModal, modalState };

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
};

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
};
